import React, { useState, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { employeeApi } from '../../api/employeeApi';
import toast from 'react-hot-toast';
import { CheckBadgeIcon, QrCodeIcon } from '@heroicons/react/24/solid';

const QRScanner = () => {
  const [employees, setEmployees] = useState([]);
  const [lastScanned, setLastScanned] = useState(null); // { id: string, name: string, time: Date }
  const [isScanning, setIsScanning] = useState(true);

  // Fetch employees to match IDs with names quickly
  useEffect(() => {
    const fetchEmps = async () => {
      try {
        const res = await employeeApi.getEmployees();
        setEmployees(res.data.data);
      } catch (error) {
        toast.error('Failed to load employee directory');
      }
    };
    fetchEmps();
  }, []);

  const handleScan = async (result) => {
    if (!result || !result[0]) return;
    
    // The QR code contains the employee ID string
    const scannedId = result[0].rawValue;
    
    // Prevent double scanning within 5 seconds
    if (lastScanned && lastScanned.id === scannedId && (new Date() - lastScanned.time) < 5000) {
      return;
    }

    const employee = employees.find(e => e._id === scannedId);
    
    if (!employee) {
      toast.error('Invalid ID Card!');
      return;
    }

    try {
      // Pause scanner while saving
      setIsScanning(false);
      
      await employeeApi.markAttendance(scannedId, {
        date: new Date().toISOString(),
        status: 'present',
        notes: 'Self Attendance (QR)'
      });
      
      // Play a success beep
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
      audio.play().catch(e => console.log('Audio blocked by browser', e));

      setLastScanned({
        id: scannedId,
        name: employee.name,
        time: new Date()
      });
      
      toast.success(`${employee.name} marked Present!`, {
        icon: '✅',
        duration: 3000
      });

    } catch (error) {
      toast.error('Failed to mark attendance for ' + employee.name);
    } finally {
      // Resume scanning after 2 seconds
      setTimeout(() => {
        setIsScanning(true);
      }, 2000);
    }
  };

  return (
    <div className="w-full pb-20 max-w-4xl mx-auto pt-4 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center justify-center gap-3">
          <QrCodeIcon className="w-10 h-10 text-[#dc343b]" />
          Self-Attendance Kiosk
        </h1>
        <p className="text-gray-500 mt-2 font-medium">Please scan your Identity Card below</p>
      </div>

      <div className="bg-white dark:bg-gray-800 p-8 rounded-[3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-gray-100 dark:border-gray-700 relative overflow-hidden">
        
        {/* Scanner Window */}
        <div className="relative max-w-sm mx-auto rounded-3xl overflow-hidden shadow-inner bg-black aspect-square flex items-center justify-center border-4 border-gray-100">
          {isScanning ? (
            <Scanner
              onScan={handleScan}
              formats={['qr_code']}
              components={{
                audio: false, // Custom audio handled
                onOff: true,
                torch: true,
                zoom: true,
                finder: true,
              }}
              styles={{
                container: { width: '100%', height: '100%' }
              }}
            />
          ) : (
            <div className="text-white flex flex-col items-center animate-pulse">
              <CheckBadgeIcon className="w-16 h-16 text-green-500 mb-2" />
              <p className="font-bold">Processing...</p>
            </div>
          )}
        </div>

        {/* Success Alert */}
        {lastScanned && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-2xl p-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
              <CheckBadgeIcon className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-black text-green-800">{lastScanned.name}</h2>
            <p className="text-green-600 font-bold mt-1">Successfully marked PRESENT</p>
            <p className="text-xs text-green-500 mt-2 font-medium">{lastScanned.time.toLocaleTimeString()}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default QRScanner;
